import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart3, Loader2 } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const PERIODS = [7, 30, 90];
const STATUS = {
  actif: { label: "Actif", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", dot: "bg-emerald-400" },
  peu_actif: { label: "Peu actif", cls: "bg-[#FFB020]/15 text-[#FFB020] border-[#FFB020]/30", dot: "bg-[#FFB020]" },
  inactif: { label: "Inactif", cls: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30", dot: "bg-[#FF3B30]" },
};
const fmtDay = (d) => new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
const fmtLast = (t) => (t ? new Date(t).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Jamais");

const StatusBadge = ({ s, id }) => {
  const st = STATUS[s];
  return (
    <span data-testid={`usage-status-${id}`} className={`inline-flex items-center gap-1.5 text-xs font-medium border rounded-full px-2.5 py-0.5 ${st.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{st.label}
    </span>
  );
};

const ConnexionsChart = ({ series }) => (
  <div className="h-64" data-testid="usage-connexions-chart">
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={series} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id="gCx" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#007AFF" stopOpacity={0.4} /><stop offset="100%" stopColor="#007AFF" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#ffffff10" vertical={false} />
        <XAxis dataKey="date" tickFormatter={fmtDay} stroke="#71717a" fontSize={11} minTickGap={20} />
        <YAxis allowDecimals={false} stroke="#71717a" fontSize={11} />
        <Tooltip labelFormatter={fmtDay} contentStyle={{ background: "#18181b", border: "1px solid #ffffff20", borderRadius: 8, fontSize: 12 }}
          formatter={(v, n) => [v, n === "connexions" ? "Connexions" : "Agents distincts"]} />
        <Area type="monotone" dataKey="connexions" stroke="#007AFF" strokeWidth={2} fill="url(#gCx)" />
        <Area type="monotone" dataKey="agents_uniques" stroke="#FFB020" strokeWidth={1.5} fill="transparent" />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

const AgentsTable = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="usage-agents-table">
      <thead className="text-zinc-500 text-xs uppercase tracking-wide">
        <tr className="border-b border-white/10">
          {["Agent", "Comptes rendus", "Rondes", "Interventions", "Dernière connexion", "Statut (7j)"].map((h, i) => (
            <th key={h} className={`py-3 px-3 font-medium ${i === 0 ? "text-left" : i < 4 ? "text-center" : "text-left"}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-white/5">
        {rows.length === 0 ? (
          <tr><td colSpan={6} className="py-8 text-center text-zinc-500">Aucun agent actif.</td></tr>
        ) : rows.map((r) => (
          <tr key={r.user_id} data-testid={`usage-agent-row-${r.user_id}`} className="hover:bg-white/[0.03] transition-colors">
            <td className="py-3 px-3 font-medium text-zinc-100">{r.name}</td>
            <td className="py-3 px-3 text-center font-code">{r.comptes_rendus}</td>
            <td className="py-3 px-3 text-center font-code">{r.rondes}</td>
            <td className="py-3 px-3 text-center font-code">{r.interventions}</td>
            <td className="py-3 px-3 text-zinc-400 font-code text-xs whitespace-nowrap">{fmtLast(r.derniere_connexion)}</td>
            <td className="py-3 px-3"><StatusBadge s={r.statut} id={r.user_id} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const UsageStats = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/dashboard/usage-stats", { params: { days } })
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [days]);

  return (
    <div className="mb-8" data-testid="usage-stats-section">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4 text-[#FFB020]" />
        <h2 className="font-display text-lg font-medium">Statistiques d'utilisation</h2>
        {loading && <Loader2 className="w-4 h-4 text-zinc-500 animate-spin" />}
      </div>
      <div className="border border-white/10 rounded-lg bg-[#121212] p-5 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <p className="font-display font-medium">Taux d'utilisation global</p>
            <p className="text-zinc-500 text-xs mt-0.5">Connexions journalières des agents · <span data-testid="usage-total-connexions">{data?.total_connexions ?? 0}</span> sur la période</p>
          </div>
          <div className="flex rounded-full border border-white/10 p-0.5" data-testid="usage-period-switch">
            {PERIODS.map((p) => (
              <button key={p} onClick={() => setDays(p)} data-testid={`usage-period-${p}`}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${days === p ? "bg-[#007AFF] text-white" : "text-zinc-400 hover:text-white"}`}>{p}j</button>
            ))}
          </div>
        </div>
        {data && <ConnexionsChart series={data.series} />}
      </div>
      <div className="border border-white/10 rounded-lg bg-[#121212] p-5">
        <p className="font-display font-medium">Activité par agent</p>
        <p className="text-zinc-500 text-xs mt-0.5 mb-3">Totaux sur les {days} derniers jours · statut sur 7 jours (Actif ≥ 5 actions, Peu actif 1–4, Inactif 0)</p>
        {data && <AgentsTable rows={data.agents} />}
      </div>
    </div>
  );
};
